import React, { useCallback, useState } from 'react';
import { Play, Pause, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useAudio } from '../context/AudioContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIMS = {
  sm: { pad: 'px-2.5 py-1.5', text: 'text-[10px]' },
  md: { pad: 'px-3 py-1.5',   text: 'text-[11px]' },
};

/**
 * AffirmationListenButton — pill that streams Rowdy's voice reading a
 * single affirmation via `/api/tts/manifest`. Audio playback runs through
 * the GLOBAL AudioContext so:
 *   • only one clip plays app-wide;
 *   • the persistent mini-player takes over navigation;
 *   • the button reflects the shared player state (Play/Pause/Loading).
 *
 * Props
 *   themeId, index, text — passed straight to the backend. `text` should
 *                          be the affirmation body (or title as fallback).
 *   size    'sm' | 'md'
 *   variant 'onDark' | 'onLight'
 *   artwork optional cover image URL for the mini-player
 *   title   optional display title for the mini-player
 */
const AffirmationListenButton = ({ themeId, index, text, size = 'sm', variant = 'onDark', artwork, title }) => {
  const { user } = useAuth();
  const { state, play, toggle } = useAudio();
  const [fetching, setFetching] = useState(false);

  // A stable-per-clip src key — the /api endpoint response is streamed to a
  // Blob URL and then handed to the global player. We track the clip via
  // the returned blob URL to know if this button represents the currently
  // playing track.
  const [myBlobUrl, setMyBlobUrl] = useState(null);
  const isThisPlaying = state.current?.src && myBlobUrl && state.current.src === myBlobUrl;
  const playing = isThisPlaying && state.playing;
  const loading = fetching || (isThisPlaying && state.loading);

  const stopBubble = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handle = useCallback(async (e) => {
    stopBubble(e);
    if (loading) return;

    // If this exact clip is loaded in the global player, just toggle.
    if (isThisPlaying) { toggle(); return; }

    if (!user) {
      toast('Sign in to hear this affirmation', {
        description: "Track your streak and unlock Rowdy's voice.",
      });
      return;
    }

    setFetching(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/tts/manifest/${themeId}/${index}.mp3`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        const msg = res.status === 402 ? "Voice paused — Rowdy is topping up his voice minutes"
                  : res.status === 429 ? 'Slow down — try again in a moment'
                  : res.status === 503 ? 'Voice service is not configured'
                  : 'Voice temporarily unavailable';
        toast.error(msg);
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      setMyBlobUrl(url);
      await play({
        src: url,
        title: title || 'Rowdy\u2019s Voice',
        subtitle: 'Manifest affirmation',
        artwork,
        kind: 'tts',
        meta: { themeId, index },
      });
    } catch {
      toast.error('Voice temporarily unavailable');
    } finally {
      setFetching(false);
    }
  }, [loading, isThisPlaying, toggle, user, themeId, index, text, artwork, title, play]);

  const dims = DIMS[size] || DIMS.sm;
  const restBg   = variant === 'onLight' ? '#173F35'               : 'rgba(248,246,240,0.15)';
  const restText = '#F8F6F0';
  const restBrdr = variant === 'onLight' ? 'rgba(214,173,96,0.55)' : 'rgba(248,246,240,0.35)';

  const label = loading ? 'Loading' : playing ? 'Pause' : 'Listen';
  const icon = loading ? <Loader2 className="w-3 h-3 animate-spin" />
             : playing ? <Pause className="w-3 h-3" />
                       : <Play className="w-3 h-3" />;

  return (
    <button
      type="button"
      onClick={handle}
      data-testid={`aff-listen-${themeId}-${index}`}
      aria-pressed={playing}
      className={`inline-flex items-center gap-1 rounded-full font-semibold uppercase tracking-wide transition-all active:scale-95 ${dims.pad} ${dims.text}`}
      style={{
        background: playing ? '#D6AD60' : restBg,
        color: playing ? '#242824' : restText,
        border: `1px solid ${playing ? '#D6AD60' : restBrdr}`,
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
      }}
    >
      {icon}
      {label}
    </button>
  );
};

export default AffirmationListenButton;
